<script lang="ts" setup>
import { WizardContainer, WizardPageDefinition } from '@/objects/Wizard';
import { ButtonComp } from '@/components';
import { Tab } from 'bootstrap';
import { ref, nextTick, computed } from 'vue';
import { useToastStore } from '@/stores';
import { onMounted } from 'vue';
import IconComp from '../IconComp.vue';

const props = withDefaults(
    defineProps<{
        /**
         * The wizard definition
         */
        wizardDefinition: WizardContainer;

        /**
         * Show the previous and next buttons
         * @default true
         */
        showPrevNext?: boolean;

        /**
         * Show the header
         * @default true
         */
        showHeader?: boolean;

        /**
         * Use the given space - if this is set the pane will have heigh of container minus the pills and footer
         */
        useGivenSpace?: boolean;

        /**
         * The page to start on a given index
         * @default 0
         */
        startOnPageIndex?: number;
    }>(),
    {
        showPrevNext: true,
        showHeader: true,
        useGivenSpace: false,
        startOnPage: 0,
    },
);

const pillContainer = ref<HTMLUListElement | null>(null);
const footer = ref<HTMLDivElement | null>(null);
const pillheight = computed(() => pillContainer.value?.offsetHeight ?? 0);
const footerheight = computed(() => footer.value?.offsetHeight ?? 0);
const heightOfPane = computed(() => {
    if (props.useGivenSpace) return { height: `calc(100% - ${pillheight.value}px - ${footerheight.value}px) !important` };
    return {};
});

const wizardPageButton = ref<HTMLAnchorElement[]>([]);

const activeWizardPage = ref<WizardPageDefinition | null>(null);

const first = () => {
    const tab = Tab.getOrCreateInstance(wizardPageButton.value[props.startOnPageIndex ?? 0]);
    tab?.show();
};

const next = () => {
    const idx = props.wizardDefinition.ActiveWizardPageIdx;
    if (idx >= wizardPageButton.value.length - 1) return;
    const tab = Tab.getOrCreateInstance(wizardPageButton.value[idx + 1]);
    tab?.show();
};

const previous = () => {
    const idx = props.wizardDefinition.ActiveWizardPageIdx;
    if (idx == 0) return;
    const tab = Tab.getOrCreateInstance(wizardPageButton.value[idx - 1]);
    tab?.show();
};

const goto = (page: WizardPageDefinition) => {
    const idx = props.wizardDefinition.WizardPages.findIndex((x) => x.Id == page.Id);
    if (idx == -1) return;
    const tab = Tab.getOrCreateInstance(wizardPageButton.value[idx]);
    tab?.show();
};

const tabShow = (e: Event, wizardPage: WizardPageDefinition) => {
    if (!props.wizardDefinition.CanSwitchToPage(wizardPage)) {
        e.preventDefault();
        useToastStore().addWarning('fieldvalidate.required');
    }
};

const tabShown = (e: Event, wizardPage: WizardPageDefinition) => {
    props.wizardDefinition.SetActiveWizardPage(wizardPage);
    activeWizardPage.value = wizardPage;
    nextTick(() => {
        emit('tabShown', wizardPage);
    });
};

const emit = defineEmits<{
    (e: 'tabShown', tab: WizardPageDefinition): void;
    (e: 'complete'): void;
}>();

/**
 * When we press the complete button
 */
const complete = () => {
    emit('complete');
};

defineExpose({
    /**
     * Go to the first page in the wizard.
     */
    next,

    /**
     * Go to the previous page in the wizard.
     */
    previous,

    /**
     * Go to a specific page in the wizard.
     * @param page The page to go to
     */
    goto,

    /**
     * The current page number in the wizard.
     * - 'first' if the first page
     * - 'last' if the last page
     * - the page number if in between
     */
    currentPage: computed(() => {
        for (let i = 0; i < props.wizardDefinition.WizardPages.length; i++) {
            if (props.wizardDefinition.WizardPages[i].Id == activeWizardPage.value?.Id) {
                return i == 0 ? 'first' : i == props.wizardDefinition.WizardPages.length - 1 ? 'last' : i + 1;
            }
        }
        return 0;
    }),

    currentPageDefinition: computed<WizardPageDefinition | null>(() => {
        return activeWizardPage.value;
    }),
});

onMounted(() => {
    first();
});
</script>

<template>
    <div class="d-flex flex-column">
        <div
            class="wizard h-100"
            :class="{ 'flex-fill': useGivenSpace }"
        >
            <ul
                class="nav nav-pills"
                v-show="showHeader"
                ref="pillContainer"
            >
                <li
                    class="nav-item flex-fill"
                    v-for="(wizardPage, i) in wizardDefinition.WizardPages"
                    :key="i"
                    :class="{ active: wizardPage.Id == activeWizardPage?.Id }"
                >
                    <a
                        ref="wizardPageButton"
                        class="nav-link"
                        :class="{ active: wizardPage.Id == activeWizardPage?.Id }"
                        :id="wizardPage.Id"
                        :data-bs-target="`#${wizardPage.Id}-pane`"
                        :aria-controls="`${wizardPage.Id}-pane`"
                        type="button"
                        data-bs-toggle="tab"
                        role="tab"
                        v-on="{
                            'show.bs.tab': (e: any) => tabShow(e, wizardPage),
                            'shown.bs.tab': (e: any) => tabShown(e, wizardPage),
                        }"
                    >
                        <IconComp
                            v-if="wizardPage.Icon"
                            :icon="wizardPage.Icon"
                            class="me-2 display-2"
                        ></IconComp>
                        <span
                            v-else
                            class="display-2 me-2"
                            >{{ `${i + 1}.` }}</span
                        >{{ $tm(wizardPage.TranslationKey) }}</a
                    >
                </li>
            </ul>
            <div
                class="tab-content"
                :style="heightOfPane"
            >
                <div
                    class="tab-pane fade"
                    :class="{ 'show active': wizardDefinition.isWizardPageActive(wizardPage), 'h-100': useGivenSpace }"
                    :id="`${wizardPage.Id}-pane`"
                    :aria-labelledby="`${wizardPage.Id}-pane`"
                    role="tabpanel"
                    tabindex="0"
                    v-for="(wizardPage, i) in wizardDefinition.WizardPages"
                    :key="i"
                >
                    <slot :name="`wizardPage-${wizardPage.Name}`"></slot>
                </div>
            </div>
        </div>
        <div
            class="w-100 d-flex justify-content-between"
            v-if="showPrevNext"
            ref="footer"
        >
            <!--TODO: Style meg!-->
            <ButtonComp
                buttonType="Secondary"
                icon="fa-arrow-left"
                @click="previous"
                >{{ $tm('general.previous') }}</ButtonComp
            >

            <ButtonComp
                v-if="wizardDefinition.ActiveWizardPageIdx == wizardDefinition.WizardPages.length - 1"
                buttonType="Primary"
                icon="fa-check"
                @click="complete"
                >{{ $tm('general.complete') }}</ButtonComp
            >

            <ButtonComp
                v-else
                buttonType="Secondary"
                icon="fa-arrow-right"
                icon-position="right"
                @click="next"
                >{{ $tm('general.next') }}</ButtonComp
            >
        </div>
    </div>
</template>
